import React, { FC, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  FormChildQuestionDto,
  FormQuestionDto,
  QuestionConfigDetailDto,
  FormSectionDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import QuestionTypeSelect from "./components/QuestionTypeSelect";
import Description from "./components/Description";
import QuestionId from "./components/QuestionId";
import Actions from "./components/Actions";
import QuestionTypeEditor from "./components/QuestionTypeEditor";
import { useQuestionEditorStore } from "./QuestionEditor.store";
import { newQuestionConfig } from "./QuestionEditor.constants";
import IsRequired from "./components/IsRequired";

import { useCreateQuestionToSection, useUpdateQuestionConfig } from "@api";
import { Box, Stack, Typography, Divider, Card } from "@components/base";
import { LanguageTabs } from "@components/common";
import { useUserStore } from "@store/user";

interface QuestionEditor {
  formId: string;
  formSection: FormSectionDto;
  formQuestion?: FormQuestionDto | FormChildQuestionDto;
  onSave: () => void;
  onCancel: () => void;
  questionConfig?: QuestionConfigDetailDto;
}

const QuestionEditor: FC<QuestionEditor> = ({
  formId,
  formSection,
  formQuestion,
  onSave,
  onCancel,
  questionConfig = newQuestionConfig,
}) => {
  const { t } = useTranslation();
  const methods = useForm();
  const { user } = useUserStore();
  const {
    language,
    setLanguage,
    isRequired,
    setIsRequired,
    newQuestionConfig,
    setNewQuestionConfig,
  } = useQuestionEditorStore();
  const {
    updateQuestionConfigAsync,
    updateQuestionConfigEnabled,
    isPendingUpdateQuestionConfig,
  } = useUpdateQuestionConfig(user, formId, formSection.id);
  const {
    createQuestionToSection,
    createQuestionToSectionEnabled,
    isPendingCreateQuestionToSection,
  } = useCreateQuestionToSection(user, formId, formSection.id);
  const editMode = !!questionConfig.id;

  const handleSubmit = async (): Promise<void> => {
    if (newQuestionConfig) {
      const dto = {
        ...newQuestionConfig,
        childQuestions: newQuestionConfig.childQuestions?.map((childQuestion) => ({
          ...childQuestion,
          unitId: childQuestion.unit?.id,
        })),
        unitId: newQuestionConfig.unit?.id,
        isRequired,
      };

      if (formQuestion) {
        await updateQuestionConfigAsync({
          questionId: (formQuestion as FormQuestionDto).formQuestionId,
          update: dto,
        });
      } else {
        await createQuestionToSection({
          sectionId: formSection.id,
          dto: dto,
        });
      }
      onSave();
    }
  };

  useEffect(() => {
    if (user) {
      setLanguage(user.localizationLanguage);
    }
  }, [user]);

  useEffect(() => {
    setNewQuestionConfig({
      ...questionConfig,
    });
    setIsRequired(formQuestion ? formQuestion.isRequired : false);
  }, [questionConfig, formQuestion]);

  return (
    ((updateQuestionConfigEnabled && editMode) ||
      (createQuestionToSectionEnabled && !editMode)) &&
    (newQuestionConfig ? (
      <FormProvider {...methods}>
        <Stack p={3} spacing={2}>
          <Typography variant="h6" gutterBottom>
            {editMode
              ? t("questionEditor.editTitle")
              : t("questionEditor.createTitle")}
          </Typography>
          <LanguageTabs language={language} setLanguage={setLanguage} />
          <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: 1 }}>
            {t("questionEditor.typeLabel")}
          </Typography>
          <QuestionTypeSelect
            questionConfig={newQuestionConfig}
            setQuestionConfig={(questionConfig) =>
              setNewQuestionConfig(questionConfig)
            }
          />
          <Card color="grey">
            <Box p={3}>
              <QuestionTypeEditor
                questionConfig={newQuestionConfig}
                setQuestionConfig={(questionConfig) =>
                  setNewQuestionConfig(questionConfig)
                }
              />
            </Box>
          </Card>
          <Description />
          <Divider />
          <Typography variant="h6">{t("questionEditor.settings")}</Typography>
          <IsRequired />
          {editMode && <QuestionId />}
          <Actions
            questionConfig={questionConfig}
            onCancel={onCancel}
            onSubmit={handleSubmit}
            disabled={
              isPendingUpdateQuestionConfig || isPendingCreateQuestionToSection
            }
          />
        </Stack>
      </FormProvider>
    ) : null)
  );
};

export default QuestionEditor;
